import React, { useEffect, useRef, useState } from 'react';
import {
  Download,
  Laptop,
  MoreVertical,
  PlusSquare,
  Share,
  Smartphone,
  Tablet,
  X,
} from 'lucide-react';
import { usePwaInstall } from '../hooks/usePwaInstall';
import type { PwaInstallPlatform } from '../types';
import { useModalAccessibility } from '../hooks/useModalAccessibility';

interface InstallInstructionsContent {
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  iconWrapperClassName: string;
  steps: React.ReactNode[];
}

interface PwaInstallControlProps {
  className?: string;
  label?: string;
  onInstructionsOpenChange?: (open: boolean) => void;
}

const PwaInstallControl: React.FC<PwaInstallControlProps> = ({
  className,
  label = 'Instalar Juris',
  onInstructionsOpenChange,
}) => {
  const [showInstructions, setShowInstructions] = useState(false);
  const [promptPending, setPromptPending] = useState(false);
  const promptPendingRef = useRef(false);
  const mountedRef = useRef(true);
  const showInstructionsRef = useRef(false);
  const instructionsOpenChangeRef = useRef(onInstructionsOpenChange);
  const { isStandalone, canPromptInstall, platform, promptInstall } = usePwaInstall();

  instructionsOpenChangeRef.current = onInstructionsOpenChange;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (showInstructionsRef.current) {
        instructionsOpenChangeRef.current?.(false);
      }
    };
  }, []);

  useEffect(() => {
    if (!isStandalone || !showInstructionsRef.current) return;

    showInstructionsRef.current = false;
    setShowInstructions(false);
    onInstructionsOpenChange?.(false);
  }, [isStandalone, onInstructionsOpenChange, showInstructions]);

  const closeInstructions = () => {
    showInstructionsRef.current = false;
    setShowInstructions(false);
    onInstructionsOpenChange?.(false);
  };

  const handleInstallClick = async () => {
    if (promptPendingRef.current) return;

    if (canPromptInstall) {
      promptPendingRef.current = true;
      if (mountedRef.current) setPromptPending(true);
      try {
        const result = await promptInstall();
        if (result !== 'unavailable') return;
      } catch {
        // Si el navegador rechaza el prompt, se muestran las instrucciones manuales.
      } finally {
        promptPendingRef.current = false;
        if (mountedRef.current) setPromptPending(false);
      }
    }

    if (!mountedRef.current) return;
    showInstructionsRef.current = true;
    setShowInstructions(true);
    onInstructionsOpenChange?.(true);
  };

  if (isStandalone) return null;

  return (
    <>
      <button
        type="button"
        onClick={handleInstallClick}
        disabled={promptPending}
        aria-busy={promptPending}
        className={className ?? 'inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-juris px-4 py-2 font-bold text-juris transition-colors hover:bg-juris-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-juris focus-visible:ring-offset-2'}
      >
        <Download size={18} aria-hidden="true" />
        <span>{label}</span>
      </button>
      {showInstructions && (
        <InstallInstructionsModal
          platform={platform}
          onClose={closeInstructions}
        />
      )}
    </>
  );
};

export const InstallInstructionsModal: React.FC<{
  onClose: () => void;
  platform: PwaInstallPlatform;
}> = ({ onClose, platform }) => {
  const content = getInstallInstructions(platform);
  const dialogRef = useRef<HTMLDivElement>(null);

  useModalAccessibility(dialogRef, { onClose });

  return (
    <div className="fixed inset-0 z-[70] flex items-end justify-center bg-canvas/85 p-4 backdrop-blur-sm motion-fade-through-fast sm:items-center" onClick={onClose}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="install-instructions-title"
        tabIndex={-1}
        className="relative max-h-[90dvh] w-full max-w-sm overflow-y-auto rounded-3xl bg-panel p-6 pb-8 shadow-2xl motion-micro-scale"
        onClick={(event) => event.stopPropagation()}
      >
        <button type="button" onClick={onClose} className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center rounded-full bg-raised text-muted hover:text-ink" aria-label="Cerrar instrucciones de instalación">
          <X size={20} />
        </button>
        <div className="mt-2 text-center">
          <div className={`mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full ${content.iconWrapperClassName}`}>
            {content.icon}
          </div>
          <h3 id="install-instructions-title" className="text-lg font-bold text-ink">{content.title}</h3>
          <p className="mb-6 mt-2 px-4 text-sm text-muted">{content.subtitle}</p>
          <div className="mx-2 space-y-3 rounded-2xl border border-line bg-canvas p-4 text-left">
            {content.steps.map((step, index) => (
              <React.Fragment key={`${platform}-${index}`}>
                <Step number={String(index + 1)} text={step} />
                {index < content.steps.length - 1 && <div className="h-px w-full bg-raised" />}
              </React.Fragment>
            ))}
          </div>
          <div className="mt-5 flex items-center justify-center gap-4 text-[11px] font-semibold uppercase text-muted">
            <span className="flex items-center"><Smartphone size={12} className="mr-1" />Móvil</span>
            <span className="flex items-center"><Tablet size={12} className="mr-1" />Tableta</span>
            <span className="flex items-center"><Laptop size={12} className="mr-1" />Ordenador</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export const getInstallInstructions = (platform: PwaInstallPlatform): InstallInstructionsContent => {
  switch (platform) {
    case 'ios':
      return {
        title: 'Instalar en iPhone o iPad',
        subtitle: 'En Apple, la instalación se hace desde el navegador.',
        icon: <Share size={26} className="text-info" />,
        iconWrapperClassName: 'bg-info-soft',
        steps: [
          <span key="ios-open">Abre Juris en <span className="font-bold text-ink">Safari</span>.</span>,
          <span key="ios-share">Pulsa <span className="font-bold text-info">Compartir</span> <Share size={12} className="mx-0.5 inline text-info" />.</span>,
          <span key="ios-add">Selecciona <span className="font-bold text-ink">«Añadir a pantalla de inicio»</span> <PlusSquare size={12} className="mx-0.5 inline" />.</span>,
        ],
      };
    case 'android':
      return {
        title: 'Instalar en Android',
        subtitle: 'Si Chrome o Edge no muestran el aviso, puedes instalarla manualmente.',
        icon: <MoreVertical size={26} className="text-ink" />,
        iconWrapperClassName: 'bg-raised',
        steps: [
          <span key="android-menu">Abre el menú del navegador <MoreVertical size={12} className="mx-0.5 inline" />.</span>,
          <span key="android-install">Busca <span className="font-bold text-ink">«Instalar aplicación»</span> o <span className="font-bold text-ink">«Añadir a pantalla principal»</span>.</span>,
          <span key="android-confirm">Confirma la instalación para abrir Juris como app.</span>,
        ],
      };
    case 'safari-desktop':
      return {
        title: 'Instalar en Mac',
        subtitle: 'Safari permite guardar Juris como app en el Dock.',
        icon: <Laptop size={26} className="text-juris" />,
        iconWrapperClassName: 'bg-juris-soft',
        steps: [
          <span key="mac-open">Abre Juris en <span className="font-bold text-ink">Safari</span>.</span>,
          <span key="mac-file">En la barra superior, ve a <span className="font-bold text-ink">Archivo</span>.</span>,
          <span key="mac-dock">Selecciona <span className="font-bold text-ink">«Añadir al Dock»</span>.</span>,
        ],
      };
    case 'chromium-desktop':
      return {
        title: 'Instalar en ordenador',
        subtitle: 'Chrome, Edge y otros navegadores compatibles pueden instalar Juris como app.',
        icon: <Laptop size={26} className="text-juris" />,
        iconWrapperClassName: 'bg-juris-soft',
        steps: [
          <span key="desktop-icon">Busca el icono de instalación en la barra de direcciones.</span>,
          <span key="desktop-menu">Si no aparece, abre el menú del navegador <MoreVertical size={12} className="mx-0.5 inline" />.</span>,
          <span key="desktop-confirm">Selecciona <span className="font-bold text-ink">«Instalar app»</span> y confirma.</span>,
        ],
      };
    default:
      return {
        title: 'Instalar Juris',
        subtitle: 'La opción depende del navegador y del dispositivo.',
        icon: <Download size={26} className="text-juris" />,
        iconWrapperClassName: 'bg-juris-soft',
        steps: [
          <span key="generic-menu">Abre el menú del navegador.</span>,
          <span key="generic-options">Busca una opción como <span className="font-bold text-ink">«Instalar app»</span>, <span className="font-bold text-ink">«Añadir a inicio»</span> o <span className="font-bold text-ink">«Añadir al Dock»</span>.</span>,
          <span key="generic-confirm">Confirma para guardar Juris como aplicación web.</span>,
        ],
      };
  }
};

const Step: React.FC<{ number: string; text: React.ReactNode }> = ({ number, text }) => (
  <div className="flex items-center gap-3">
    <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border border-line bg-panel text-xs font-extrabold text-juris shadow-sm">{number}</span>
    <span className="text-xs font-medium text-muted">{text}</span>
  </div>
);

export default PwaInstallControl;
